import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import { api } from "../lib/api";
import { SECTION_COMPONENTS } from "../Sections";
import { Logo } from "../components/Logo";

/**
 * Server-rendered print view used by the headless-Chrome PDF exporter.
 *
 * This route is intentionally public — it renders only content already exposed
 * by GET /api/content (no investor-scoped data). The admin PDF endpoint is the
 * one that is auth-protected.
 *
 * Query params:
 *   - appendix=1   include appendix slides (default 0)
 *   - theme=light  (default dark) — not implemented yet, placeholder
 */
export default function PrintDeck() {
  const [params] = useSearchParams();
  const includeAppendix = params.get("appendix") === "1";

  const [content, setContent] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Signal to the renderer that we're starting
    window.__deck_ready = false;
    api.get("/content")
      .then((r) => setContent(r.data))
      .catch((e) => setError(e?.message || "Failed to load content"));
  }, []);

  // Synthetic "founder preview" investor so sandbox / business_model slides
  // never render in the PDF (they are per-investor by design).
  const investor = useMemo(() => ({
    name: "PDF Export",
    email: "",
    firm: "",
    sandbox_access: false,
    business_model_access: false,
    preview: true,
  }), []);

  const sections = useMemo(() => {
    if (!content) return [];
    return content.sections_order.filter((s) => {
      if (s.id === "sandbox") return false;
      if (s.id === "business_model") return false;
      if (s.appendix && !includeAppendix) return false;
      return true;
    });
  }, [content, includeAppendix]);

  const data = useMemo(() => (content ? { ...content, investor } : null), [content, investor]);

  // Once every slide element is in the DOM, flag ready so Chrome can snapshot
  useEffect(() => {
    if (!data || !sections.length) return;
    // Give the browser two paint cycles for images, fonts and framer-motion to settle
    const t1 = requestAnimationFrame(() => {
      const t2 = requestAnimationFrame(() => {
        setTimeout(() => { window.__deck_ready = true; }, 400);
      });
      return () => cancelAnimationFrame(t2);
    });
    return () => cancelAnimationFrame(t1);
  }, [data, sections]);

  if (error) {
    return <div style={{ padding: 40, fontFamily: "monospace", color: "#F87171" }}>Print error: {error}</div>;
  }
  if (!data) {
    return <div style={{ padding: 40, fontFamily: "monospace", color: "#94A3B8" }}>Loading…</div>;
  }

  const noop = () => {};
  const common = { onActive: noop, registerRef: noop };

  return (
    <MotionConfig reducedMotion="always" transition={{ duration: 0 }}>
      <div className="print-deck">
        {/* Cover page */}
        <section className="print-page print-cover">
          <div className="print-cover-inner">
            <Logo className="h-10 w-auto" />
            <div className="print-cover-eyebrow">Investor Deck · Confidential</div>
            <h1 className="print-cover-title">{content.brand.name || "Streamline360"}</h1>
            <div className="print-cover-sub">{content.brand.tagline || "Industrial AI — Seed Round"}</div>
            <div className="print-cover-meta">
              <span>{new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
              <span>{sections.length} slides{includeAppendix ? " · incl. appendix" : ""}</span>
            </div>
          </div>
        </section>

        {sections.map((s) => {
          const C = SECTION_COMPONENTS[s.id];
          if (!C) return null;
          return (
            <section key={s.id} className="print-page" data-slide-id={s.id}>
              <div className="print-page-inner">
                <C data={data} common={common} />
              </div>
            </section>
          );
        })}
      </div>
    </MotionConfig>
  );
}
